import { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  Chip, CircularProgress, Alert,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import api from '../../config/api';
import useStore from '../../config/store';

const KINDS = ['contract', 'onboarding', 'annex'];

/**
 * Formatos de Izydocs (PLAN_ERP.md §15.4, fase B2, 2026-10-20), solo lectura:
 * qué formato usa Amanda para la firma y cuándo, qué datos pide cada uno y
 * cuáles están identificados pero pendientes. Las plantillas viven en Izydocs
 * (el texto se cambia allá); el catálogo viene del backend, el mismo que usa
 * el envío a firma.
 */
const ErpIzydocsFormatsTab = () => {
  const { t } = useTranslation();
  const { organization, authToken } = useStore.getState();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const k = (key) => `erp.izydocsFormats.${key}`;

  useEffect(() => {
    if (!organization) return undefined;
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.get('/erp/izydocs/formats', {
          params: { organizationId: organization },
          headers: { Authorization: `Bearer ${authToken}` },
        });
        if (cancelled) return;
        if (response.data.response?.status_response === 'OK') setData(response.data.response.data);
        else setError(t(k('loadError')));
      } catch (err) {
        console.error(err);
        if (!cancelled) setError(t(k('loadError')));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
  if (error || !data) return <Alert severity="error">{error || t(k('loadError'))}</Alert>;

  const provider = data.provider || {};
  const operations = (provider.pending || []).map((op) => t(`erp.contracts.signature.operations.${op}`)).join(', ');
  const company = (format) =>
    t(k(`companies.${format.serviceMode || 'general'}`), { id: format.branchOfficeId });

  return (
    <Box>
      <Typography variant="h6" fontWeight={700}>{t(k('title'))}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{t(k('intro'))}</Typography>
      {provider.configured === false && <Alert severity="warning" sx={{ mb: 2 }}>{t(k('notConfigured'))}</Alert>}
      {provider.configured && operations && <Alert severity="info" sx={{ mb: 2 }}>{t(k('notWired'), { list: operations })}</Alert>}

      {KINDS.map((kind) => {
        const rows = (data.formats || []).filter((f) => f.kind === kind);
        if (rows.length === 0) return null;
        return (
          <Box key={kind} sx={{ mb: 3 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>{t(k(`kinds.${kind}`))}</Typography>
            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t(k('columns.id'))}</TableCell>
                    <TableCell>{t(k('columns.name'))}</TableCell>
                    <TableCell>{t(k('columns.company'))}</TableCell>
                    <TableCell>{t(k('columns.usage'))}</TableCell>
                    <TableCell>{t(k('columns.tags'))}</TableCell>
                    <TableCell>{t(k('columns.status'))}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((format) => (
                    <TableRow key={format.documentId}>
                      <TableCell>{format.documentId}</TableCell>
                      <TableCell>{format.name}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{company(format)}</TableCell>
                      <TableCell>{t(k(`variants.${format.variant}`))}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 12, maxWidth: 360 }}>
                        {format.tags.length > 0 ? format.tags.join(', ') : t(k('noTags'))}
                      </TableCell>
                      <TableCell>
                        <Chip size="small" color={format.status === 'active' ? 'success' : 'default'}
                          label={t(k(`statuses.${format.status}`))} />
                        {format.pendingReason && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            {t(k(`pendingReasons.${format.pendingReason}`))}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        );
      })}
    </Box>
  );
};

export default ErpIzydocsFormatsTab;
